import { useState } from "react";
import { useNavigate } from "react-router";
import { OnboardingLayout } from "./OnboardingLayout";
import { saveOnboarding } from "../../api/onboarding";
import { ApiError } from "../../api/client";
import type { ExamChoice } from "../../lib/catalog";
import { setActiveExam } from "../../lib/activeExam";

const EXAMS: { id: ExamChoice; initial: string; name: string; full: string; blurb: string }[] = [
  {
    id: "JAMB",
    initial: "J",
    name: "JAMB",
    full: "(UTME)",
    blurb: "University entry — 4 subjects, computer-based.",
  },
  {
    id: "WAEC",
    initial: "W",
    name: "WAEC",
    full: "(SSCE)",
    blurb: "School certificate — May/June or Nov/Dec sitting.",
  },
];

/**
 * Screen 7 — tap selects and advances. No confirm button.
 *
 * The two cards are identical in weight. Exam neutrality is a spec rule, and a
 * visual preference here would read as advice about which exam to sit.
 */
export function ExamPickScreen() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * Saved here, not carried in navigation state.
   *
   * The exam decides which subjects the next screen offers and how readiness
   * is weighted. Passed only in `location.state` it would vanish on a refresh,
   * and it never reached the database at all.
   */
  const pick = async (examType: ExamChoice | "BOTH") => {
    if (saving) return;
    setSaving(true);
    setError(null);

    try {
      await saveOnboarding({ examType });
      if (examType === "BOTH") {
        // Two profiles, JAMB first: its subjects, then WAEC's.
        setActiveExam("JAMB");
        void navigate("/subjects", { state: { exam: "JAMB", next: "WAEC" } });
      } else {
        setActiveExam(examType);
        void navigate("/subjects", { state: { exam: examType } });
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "We couldn't save that. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnboardingLayout
      step={4}
      title="Which exam are you facing?"
      subtitle="Tap one — it takes you straight to your subjects."
      action={
        <div className="flex flex-col gap-2">
          {error ? (
            <p role="alert" className="m-0 text-center text-[12.5px] text-negative">
              {error}
            </p>
          ) : null}
          <p className="m-0 text-center text-[12.5px] leading-[1.55] text-text-muted">
            One payment covers both. You can also add the other exam from Profile later.
          </p>
        </div>
      }
    >
      {EXAMS.map((exam) => (
        <button
          key={exam.id}
          type="button"
          disabled={saving}
          onClick={() => void pick(exam.id)}
          className="flex items-start gap-3.5 rounded-[var(--radius-card)] border border-border bg-surface p-4 text-left active:border-accent"
        >
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[var(--radius-control)] bg-surface-high font-[family-name:var(--font-display)] text-[17px] font-bold text-accent">
            {exam.initial}
          </span>
          <span className="flex flex-col gap-1">
            <span className="text-[15px] font-semibold">
              {exam.name} <span className="font-normal text-text-muted">{exam.full}</span>
            </span>
            <span className="text-[12.5px] leading-[1.45] text-text-muted">{exam.blurb}</span>
            <span className="tabular pt-0.5 text-[11px] text-text-muted">2027 exam cycle</span>
          </span>
        </button>
      ))}
      <button
        type="button"
        disabled={saving}
        onClick={() => void pick("BOTH")}
        className="flex items-start gap-3.5 rounded-[var(--radius-card)] border border-border bg-surface p-4 text-left active:border-accent"
      >
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[var(--radius-control)] bg-surface-high font-[family-name:var(--font-display)] text-[15px] font-bold text-accent">
          J+W
        </span>
        <span className="flex flex-col gap-1">
          <span className="text-[15px] font-semibold">Both JAMB and WAEC</span>
          <span className="text-[12.5px] leading-[1.45] text-text-muted">
            Separate subjects and readiness for each — switch any time.
          </span>
        </span>
      </button>
    </OnboardingLayout>
  );
}
